/**
 * Avatar IDs Constant
 * Provides strict typing and autocomplete for avatar identifiers
 * Prevents typos by using const object instead of string literals
 */

/** Avatars users can pick for themselves (excludes reserved e.g. monkey). */
export const AVATAR_IDS = {
  BLUE_WITH_HAIR: 'blue-with-hair',
  MOYKAN_BALL: 'moykan-ball',
  BLUE_WITH_CAP: 'blue-with-cap',
  GREEN_WHITE: 'green-white',
  LIGHT_BLUE: 'light-blue',
  LIGHT_RED: 'light-red',
  WHITE_RED: 'white-red',
  WHITE_YELLOW: 'white-yellow',
  MAC: 'mac',
  // Complete-look girls: single illustration, picked as ready avatars (not customizer bases).
  ALL_STAR: 'girl4',
  TRAILBLAZER: 'girl5',
  SKY_CAPTAIN: 'girl6',
} as const;

export type AvatarId = (typeof AVATAR_IDS)[keyof typeof AVATAR_IDS];

/** Default avatar used when avatar ID is missing or deprecated. */
export const DEFAULT_AVATAR_ID: AvatarId = AVATAR_IDS.BLUE_WITH_HAIR;

/**
 * Deprecated/copyrighted avatar IDs kept only for backward-compatibility
 * fallback handling when old user records still contain them.
 */
export const DEPRECATED_AVATAR_IDS = {
  YELLOW_BEER: 'yellow-beer',
  ARSENAL: 'arsenal',
  MANFAN: 'manfan',
  REAL_MADRID_1: 'real-madrid-1',
  BARCA_1: 'barca1',
  BARCA_2: 'barca2',
} as const;

const VALID_AVATAR_IDS_SET = new Set(Object.values(AVATAR_IDS));

/**
 * Normalize unknown/deprecated avatar IDs to the current default avatar.
 * Use this for data coming from backend or legacy records.
 */
export const normalizeAvatarId = (avatarId: string | null | undefined): AvatarId => {
  if (!avatarId) return DEFAULT_AVATAR_ID;
  return VALID_AVATAR_IDS_SET.has(avatarId as AvatarId)
    ? (avatarId as AvatarId)
    : DEFAULT_AVATAR_ID;
};

